// Lista de clientes — /identities
// - useInfiniteQuery (F3) com filtros e busca debounced
// - Paginação prev/next por página (cursor preservado — DR-032)
// - Skeleton durante loading inicial (F2)
// - Empty state com CTA (F7)
// - Error state com botão Tentar de novo (F8)

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Search, Users } from 'lucide-react';

import type { ClientStatus } from '@control-center/shared';

import { ClientTable, ClientTableSkeleton } from '../../components/clients/ClientTable';
import { PillButton } from '../../components/clients/PillButton';
import { Select } from '../../components/clients/form-primitives';
import { EmptyState as RichEmptyState } from '../../components/common/EmptyState';
import { PageHelp } from '../../components/common/PageHelp';
import { Pagination } from '../../components/common/Pagination';
import { useClientsInfinite, type ClientsListFilters } from '../../hooks/use-clients';
import { extractErrorMessage } from '../../lib/errors';
import type { ClientCreatedVia } from '../../lib/clients-api';

const STATUS_FILTERS: { value: '' | ClientStatus; label: string }[] = [
  { value: '', label: 'Todos os status' },
  { value: 'lead', label: 'Lead' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'em_implantacao', label: 'Em implantação' },
  { value: 'em_teste', label: 'Em teste' },
  { value: 'inadimplente', label: 'Inadimplente' },
  { value: 'suspenso', label: 'Suspenso' },
  { value: 'cancelado', label: 'Cancelado' },
];

const ORIGIN_FILTERS: { value: '' | ClientCreatedVia; label: string }[] = [
  { value: '', label: 'Todas as origens' },
  { value: 'control_center_admin', label: 'Cadastro manual' },
  { value: 'saas_direct', label: 'SaaS Direct' },
  { value: 'migration', label: 'Migração' },
  { value: 'bootstrap_import', label: 'Bootstrap' },
];

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

export function ClientsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | ClientStatus>('');
  const [originFilter, setOriginFilter] = useState<'' | ClientCreatedVia>('');

  const debouncedQuery = useDebouncedValue(query, 300);

  const filters: ClientsListFilters = useMemo(() => {
    const f: ClientsListFilters = {};
    if (debouncedQuery.trim()) f.q = debouncedQuery.trim();
    if (statusFilter) f.status = statusFilter;
    if (originFilter) f.created_via = originFilter;
    return f;
  }, [debouncedQuery, statusFilter, originFilter]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useClientsInfinite(filters);

  // Navegação por página: o useInfiniteQuery acumula as páginas em cache
  // (cursor — DR-032); aqui renderizamos UMA por vez. Voltar é instantâneo;
  // avançar busca a próxima só quando ela ainda não foi carregada.
  const [pageIndex, setPageIndex] = useState(0);
  useEffect(() => setPageIndex(0), [filters]);

  const pages = useMemo(() => data?.pages ?? [], [data]);
  // Clamp: refetch após mutation pode reduzir o nº de páginas em cache
  const safeIndex = Math.min(pageIndex, Math.max(pages.length - 1, 0));
  const currentPage = pages[safeIndex];
  const clients = currentPage?.items ?? [];
  const total = currentPage?.meta.total_estimate ?? null;

  const rangeStart = useMemo(
    () =>
      clients.length === 0
        ? 0
        : pages.slice(0, safeIndex).reduce((acc, p) => acc + p.items.length, 0) + 1,
    [pages, safeIndex, clients.length],
  );
  const rangeEnd = rangeStart === 0 ? 0 : rangeStart + clients.length - 1;

  const hasPrev = safeIndex > 0;
  const hasNext = safeIndex < pages.length - 1 || Boolean(hasNextPage);

  const goNext = () => {
    if (safeIndex < pages.length - 1) {
      setPageIndex(safeIndex + 1);
      return;
    }
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage().then((res) => {
        if (!res.isError) setPageIndex(safeIndex + 1);
      });
    }
  };

  const hasActiveFilters = !!debouncedQuery || !!statusFilter || !!originFilter;

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="m-0 text-[30px] font-bold leading-none tracking-[-0.025em] text-ink">
              Identidades
            </h1>
            <p className="mt-[6px] max-w-[560px] text-[13px] leading-[1.5] text-muted">
              Clientes do ecossistema — manual, auth.owlflow, saas_direct ou bootstrap.
            </p>
          </div>
          <PageHelp id="identities" />
        </div>
        <div className="flex flex-wrap gap-2">
          <PillButton
            onClick={() => refetch()}
            icon={
              <RefreshCw
                size={14}
                strokeWidth={2}
                className={isFetching ? 'animate-spin' : undefined}
              />
            }
          >
            Atualizar
          </PillButton>
          <PillButton
            solid
            onClick={() => navigate('/identities/new')}
            icon={<Plus size={14} strokeWidth={2} />}
          >
            Novo cliente
          </PillButton>
        </div>
      </header>

      {/* Filtros */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_220px_220px]">
        <div className="relative">
          <Search
            size={14}
            strokeWidth={2}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-2"
            aria-hidden
          />
          <input
            id="cc-clients-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nome, documento ou e-mail…"
            aria-label="Buscar clientes"
            className="h-10 w-full rounded-[10px] border border-line bg-panel pl-9 pr-3 text-[13px] text-ink placeholder:text-muted-3 outline-none transition-colors focus:border-line-2"
          />
        </div>
        <Select
          aria-label="Filtrar por status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | ClientStatus)}
        >
          {STATUS_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filtrar por origem"
          value={originFilter}
          onChange={(e) => setOriginFilter(e.target.value as '' | ClientCreatedVia)}
        >
          {ORIGIN_FILTERS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </div>

      {/* Conteúdo */}
      {isLoading ? (
        <ClientTableSkeleton />
      ) : isError ? (
        <ErrorState
          message={extractErrorMessage(error, 'Não conseguimos carregar os clientes.')}
          onRetry={() => refetch()}
        />
      ) : clients.length === 0 ? (
        hasActiveFilters ? (
          <FilteredEmpty
            onClear={() => {
              setQuery('');
              setStatusFilter('');
              setOriginFilter('');
            }}
          />
        ) : (
          <RichEmptyState
            icon={Users}
            title="Sem clientes ainda"
            description="Cadastre a primeira identidade pra começar a vincular planos, assinaturas e cobranças."
            bullets={[
              'PF ou PJ — com CPF/CNPJ pra emissão de NF',
              'Endereço puxa automático pelo CEP',
              'Pode importar quem já paga direto no Reservei/OwlTask em /identities/import',
            ]}
            primaryAction={{
              label: 'Cadastrar primeiro cliente',
              icon: <Plus size={14} strokeWidth={2} />,
              onClick: () => navigate('/identities/new'),
            }}
            secondaryAction={{
              label: 'Importar de SaaS conectado',
              href: '/identities/import',
            }}
          />
        )
      ) : (
        <>
          <ClientTable clients={clients} />
          <Pagination
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPrev={() => setPageIndex(Math.max(safeIndex - 1, 0))}
            onNext={goNext}
            isLoading={isFetchingNextPage}
            noun="cliente"
          />
        </>
      )}
    </div>
  );
}

function FilteredEmpty({ onClear }: { onClear: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-line bg-panel py-12 text-center">
      <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">
        Nenhum cliente bate com esses filtros.
      </h2>
      <p className="mx-auto mt-1 max-w-[360px] text-[12px] text-muted">
        Tente afrouxar a busca ou limpar os filtros.
      </p>
      <div className="mt-4 flex justify-center">
        <PillButton onClick={onClear}>Limpar filtros</PillButton>
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-bad/40 bg-panel p-6">
      <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">
        Não conseguimos carregar os clientes.
      </h2>
      <p className="mt-1 text-[12px] text-muted">{message}</p>
      <div className="mt-4">
        <PillButton onClick={onRetry} icon={<RefreshCw size={14} strokeWidth={2} />}>
          Tentar de novo
        </PillButton>
      </div>
    </div>
  );
}
